import { ATTACHMENT_ACCEPT, RECORD_DOC_TYPES } from '@loot/shared';
import { useMutation } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { Download } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api, ApiError } from '../../api.ts';
import { FormModal, FormSection } from '../../components/FormModal.tsx';
import { MasterBadge } from '../../components/MasterBadge.tsx';
import { MasterDeleteButton } from '../../components/MasterDelete.tsx';
import {
  Badge,
  Button,
  EmptyRow,
  Field,
  Input,
  Panel,
  Select,
  Table,
} from '../../components/ui.tsx';
import { checkFile } from '../purchasing/Attachments.tsx';
import { useFormat } from '../purchasing/common.tsx';

export interface RecordAttachment {
  id: string;
  fileName: string;
  sizeBytes: number;
  docType: string;
  classification: string;
  version: number;
  createdAt: string;
}
export interface RecordEvent {
  id: string;
  kind: string;
  note: string | null;
  actorName: string;
  byMaster?: boolean;
  createdAt: string;
}

const LEVELS = ['internal', 'restricted', 'confidential'] as const;

/**
 * Cadastro longo em janela com os blocos em abas: guarda a aba aberta, marca as abas com campo
 * errado e, quando o servidor recusa o salvamento, abre a primeira aba com erro.
 */
export function useSectionTabs<T extends string>(
  sections: readonly T[],
  error: unknown,
  sectionOf: (field: string) => T | undefined,
) {
  const [tab, setTab] = useState<T>(sections[0]!);
  const [seen, setSeen] = useState(error);
  const issues = error instanceof ApiError ? error.issues : [];
  const withError = new Set(issues.map((i) => sectionOf(i.path.split('.')[0]!)));
  if (error !== seen) {
    setSeen(error);
    const first = sections.find((s) => withError.has(s));
    if (first) setTab(first);
  }
  return { tab, setTab, withError };
}

/** Etiqueta do sigilo: neutra para interna, destaque para restrita e confidencial. */
export function ConfidentialityBadge({ level }: { level: string }) {
  const { t } = useTranslation();
  return (
    <Badge tone={level === 'internal' ? 'neutral' : 'accent'}>
      {t(`legal.confidentiality.${level}`)}
    </Badge>
  );
}

/**
 * Documentos de contrato, demanda externa ou marca: lista, baixar, anexar e nova versão. Anexar e
 * nova versão abrem a janela "Anexar documento" (guia, variação 4).
 */
export function RecordDocuments({
  base,
  attachments,
  canEdit,
  onChange,
}: {
  /** Endereço do registro na API, ex.: /legal/contracts/<id>. */
  base: string;
  attachments: RecordAttachment[];
  canEdit: boolean;
  onChange: () => Promise<unknown> | void;
}) {
  const { t } = useTranslation();
  const fmt = useFormat();
  const [target, setTarget] = useState<{ supersedes?: RecordAttachment } | null>(null);
  return (
    <Panel
      title={t('legal.records.documents')}
      action={
        canEdit && (
          <Button variant="secondary" onClick={() => setTarget({})}>
            {t('legal.records.attachDocument')}
          </Button>
        )
      }
    >
      <Table
        head={[
          t('legal.records.file'),
          t('legal.records.docType'),
          t('legal.attachments.classification'),
          '',
        ]}
      >
        {attachments.length === 0 && (
          <EmptyRow colSpan={4}>
            {canEdit ? t('legal.records.noDocumentsHint') : t('legal.records.noDocuments')}
          </EmptyRow>
        )}
        {attachments.map((a) => (
          <tr key={a.id}>
            <td>
              <a
                href={`/api${base}/attachments/${a.id}`}
                className="inline-flex items-center gap-1 text-brand-text hover:underline"
              >
                <Download size={14} aria-hidden />
                {a.fileName}
              </a>
              <div className="text-xs text-muted">
                {t('legal.records.versionLine', {
                  version: a.version,
                  size: fmt.bytes(a.sizeBytes),
                  when: fmt.dateTime(a.createdAt),
                })}
              </div>
            </td>
            <td>
              {a.docType === 'signed' ? (
                <Badge tone="ok">{t('legal.records.docTypes.signed')}</Badge>
              ) : (
                t(`legal.records.docTypes.${a.docType}`)
              )}
            </td>
            <td>
              <ConfidentialityBadge level={a.classification} />
            </td>
            <td className="text-right">
              <div className="flex justify-end gap-2">
                {canEdit && (
                  <Button variant="secondary" onClick={() => setTarget({ supersedes: a })}>
                    {t('legal.attachments.replace')}
                  </Button>
                )}
                <MasterDeleteButton table="legal.record_attachment" id={a.id} compact />
              </div>
            </td>
          </tr>
        ))}
      </Table>
      {target && (
        <AttachDialog
          base={base}
          supersedes={target.supersedes}
          onClose={() => setTarget(null)}
          onDone={async () => {
            await onChange();
            setTarget(null);
          }}
        />
      )}
    </Panel>
  );
}

/** Janela "Anexar documento" ou "Nova versão": tipo, sigilo e arquivo (com nome e tamanho). */
function AttachDialog({
  base,
  supersedes,
  onClose,
  onDone,
}: {
  base: string;
  supersedes?: RecordAttachment;
  onClose: () => void;
  onDone: () => Promise<void>;
}) {
  const { t } = useTranslation();
  const fmt = useFormat();
  // A nova versão já começa com o tipo e o sigilo do documento que ela substitui.
  const [docType, setDocType] = useState<string>(supersedes?.docType ?? 'signed');
  const [classification, setClassification] = useState<string>(
    supersedes?.classification ?? 'internal',
  );
  const [file, setFile] = useState<File | null>(null);
  const send = useMutation({
    mutationFn: async () => {
      if (!file) return;
      const problem = checkFile(file);
      if (problem) throw problem;
      const body = new FormData();
      body.append('docType', docType);
      body.append('classification', classification);
      if (supersedes) body.append('supersedesId', supersedes.id);
      body.append('file', file, file.name);
      return api.upload(`${base}/attachments`, body);
    },
    onSuccess: onDone,
  });
  return (
    <FormModal
      open
      size="md"
      title={supersedes ? t('legal.records.newVersionTitle') : t('legal.records.attachDocument')}
      description={
        supersedes ? t('legal.records.newVersionHint', { name: supersedes.fileName }) : undefined
      }
      dirty={file !== null}
      saving={send.isPending}
      error={send.error}
      saveLabel={t('legal.records.attach')}
      onSave={() => file && send.mutate()}
      onClose={onClose}
    >
      <FormSection columns={1}>
        <Field label={t('legal.records.docType')} required>
          <Select value={docType} onChange={(e) => setDocType(e.target.value)} required>
            {RECORD_DOC_TYPES.map((d) => (
              <option key={d} value={d}>
                {t(`legal.records.docTypes.${d}`)}
              </option>
            ))}
          </Select>
        </Field>
        <Field
          label={t('legal.attachments.classification')}
          hint={t('legal.records.classificationHint')}
          required
        >
          <Select
            value={classification}
            onChange={(e) => setClassification(e.target.value)}
            required
          >
            {LEVELS.map((c) => (
              <option key={c} value={c}>
                {t(`legal.confidentiality.${c}`)}
              </option>
            ))}
          </Select>
        </Field>
        <Field
          label={t('legal.records.file')}
          hint={
            file
              ? t('legal.records.chosenFile', { name: file.name, size: fmt.bytes(file.size) })
              : t('legal.records.fileHint')
          }
          required
        >
          <Input
            type="file"
            accept={ATTACHMENT_ACCEPT}
            required
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
          />
        </Field>
      </FormSection>
    </FormModal>
  );
}

/** Histórico do registro (auditoria): quando, quem e o que mudou. */
export function RecordHistory({ events }: { events: RecordEvent[] }) {
  const { t } = useTranslation();
  const fmt = useFormat();
  return (
    <Panel title={t('legal.history.title')}>
      <Table head={[t('legal.history.when'), t('legal.history.who'), t('legal.history.what'), '']}>
        {events.length === 0 && <EmptyRow colSpan={4}>{t('legal.history.empty')}</EmptyRow>}
        {events.map((e) => (
          <tr key={e.id}>
            <td className="whitespace-nowrap text-muted">{fmt.dateTime(e.createdAt)}</td>
            <td>
              {e.actorName}
              {e.byMaster && (
                <>
                  {' '}
                  <MasterBadge />
                </>
              )}
            </td>
            <td>
              {t(`legal.records.events.${e.kind}`)}
              {e.note && <div className="text-xs text-muted">{e.note}</div>}
            </td>
            <td className="text-right">
              <MasterDeleteButton table="legal.record_event" id={e.id} compact />
            </td>
          </tr>
        ))}
      </Table>
    </Panel>
  );
}

const indicatorBox =
  'flex flex-col gap-0.5 rounded-lg border bg-sheet p-3 text-left shadow-sm transition';

/** Cartão de indicador que também filtra a lista (só leitura: `StatCard`, em ui.tsx). */
export function Indicator({
  label,
  value,
  active,
  onClick,
  tone,
}: {
  label: string;
  value: number;
  active?: boolean;
  onClick: () => void;
  tone?: 'danger' | 'accent';
}) {
  const number = (
    <span
      className={`text-2xl font-semibold tabular-nums ${tone === 'danger' && value > 0 ? 'text-danger' : ''} ${tone === 'accent' && value > 0 ? 'text-brand-text' : ''}`}
    >
      {value}
    </span>
  );
  return (
    <button
      type="button"
      aria-pressed={!!active}
      onClick={onClick}
      className={`${indicatorBox} hover:border-accent ${active ? 'border-accent bg-brand-soft' : 'border-rule'}`}
    >
      {number}
      <span className="text-xs text-muted">{label}</span>
    </button>
  );
}

/** Indicador que abre a lista correspondente (painel jurídico), sem recarregar a página. */
export function IndicatorLink({
  label,
  value,
  href,
  tone,
}: {
  label: string;
  value: number;
  href: string;
  tone?: 'danger' | 'accent';
}) {
  const navigate = useNavigate();
  return (
    <a
      href={href}
      onClick={(e) => {
        if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
        e.preventDefault();
        void navigate({ href });
      }}
      className={`${indicatorBox} border-rule hover:border-accent`}
    >
      <span
        className={`text-2xl font-semibold tabular-nums ${tone === 'danger' && value > 0 ? 'text-danger' : ''} ${tone === 'accent' && value > 0 ? 'text-brand-text' : ''}`}
      >
        {value}
      </span>
      <span className="text-xs text-muted">{label}</span>
    </a>
  );
}

/** Lê o arquivo escolhido e devolve o conteúdo em base64 (para simular e gravar importações). */
export async function fileToBase64(f: File) {
  const buffer = new Uint8Array(await f.arrayBuffer());
  let binary = '';
  for (let i = 0; i < buffer.length; i += 0x8000) {
    binary += String.fromCharCode(...buffer.subarray(i, i + 0x8000));
  }
  return { fileName: f.name, contentBase64: btoa(binary) };
}

export interface ImportRow {
  row: number;
  status: string;
  errors: string[];
  warnings: string[];
  entitySuggestions: { id: string; name: string }[];
  partnerSuggestions?: { id: string; name: string }[];
  changes?: string[];
}
export type Resolutions = Record<string, { legalEntityId?: string; partnerId?: string | null }>;

const ROW_TONE: Record<string, 'ok' | 'off' | 'accent' | 'info'> = {
  new: 'ok',
  update: 'info',
  error: 'off',
  pending: 'info',
};

/**
 * Linhas da prévia de importação com o assistente: nas pendentes, a pessoa escolhe a empresa
 * (as sugestões por nome parecido aparecem primeiro) e, quando houver, o parceiro sugerido.
 * Nada liga sozinho por semelhança; depois de escolher, simula de novo antes de gravar.
 */
export function ImportRows({
  rows,
  describe,
  entities,
  resolutions,
  onResolve,
  disabled,
}: {
  rows: ImportRow[];
  describe: (r: ImportRow) => { title: string; detail: string };
  entities: { id: string; name: string; code?: string | null }[];
  resolutions: Resolutions;
  onResolve: (row: number, patch: { legalEntityId?: string; partnerId?: string | null }) => void;
  disabled: boolean;
}) {
  const { t } = useTranslation();
  return (
    <Table
      head={[
        { label: t('legal.import.row'), align: 'right' },
        t('legal.import.record'),
        t('fields.status'),
        t('legal.import.issues'),
        t('legal.import.resolve'),
      ]}
    >
      {rows.map((r) => {
        const d = describe(r);
        const choice = resolutions[String(r.row)] ?? {};
        const askEntity = r.status === 'pending' || !!choice.legalEntityId;
        const partners = r.partnerSuggestions ?? [];
        const suggested = new Set(r.entitySuggestions.map((e) => e.id));
        return (
          <tr key={r.row}>
            <td className="text-right text-xs tabular-nums">{r.row}</td>
            <td>
              {d.title}
              <div className="text-xs text-muted">{d.detail}</div>
            </td>
            <td>
              <Badge tone={ROW_TONE[r.status] ?? 'neutral'}>
                {t(`legal.import.statuses.${r.status}`)}
              </Badge>
            </td>
            <td className="text-xs">
              {r.errors.map((e) => (
                <div key={e} className="text-danger">
                  {e}
                </div>
              ))}
              {r.warnings.map((w) => (
                <div key={w} className="text-muted">
                  {w}
                </div>
              ))}
              {r.changes && r.changes.length > 0 && (
                <div className="text-muted">
                  {t('legal.import.changes', { fields: r.changes.join(', ') })}
                </div>
              )}
            </td>
            <td className="min-w-48 text-xs">
              <div className="flex flex-col gap-1">
                {askEntity && (
                  <Select
                    aria-label={t('legal.import.chooseEntity')}
                    disabled={disabled}
                    value={choice.legalEntityId ?? ''}
                    onChange={(e) =>
                      onResolve(r.row, { legalEntityId: e.target.value || undefined })
                    }
                  >
                    <option value="">{t('legal.import.chooseEntity')}</option>
                    {r.entitySuggestions.map((e) => (
                      <option key={e.id} value={e.id}>
                        {t('legal.import.suggested', { name: e.name })}
                      </option>
                    ))}
                    {entities
                      .filter((e) => !suggested.has(e.id))
                      .map((e) => (
                        <option key={e.id} value={e.id}>
                          {e.code ? `${e.code} · ${e.name}` : e.name}
                        </option>
                      ))}
                  </Select>
                )}
                {(partners.length > 0 || choice.partnerId) && (
                  <Select
                    aria-label={t('legal.import.choosePartner')}
                    disabled={disabled}
                    value={choice.partnerId ?? ''}
                    onChange={(e) => onResolve(r.row, { partnerId: e.target.value || null })}
                  >
                    <option value="">{t('legal.import.noPartner')}</option>
                    {partners.map((x) => (
                      <option key={x.id} value={x.id}>
                        {t('legal.import.suggested', { name: x.name })}
                      </option>
                    ))}
                  </Select>
                )}
              </div>
            </td>
          </tr>
        );
      })}
    </Table>
  );
}

/** Junta a escolha nova às anteriores (sem guardar escolhas vazias). */
export function mergeResolution(
  all: Resolutions,
  row: number,
  patch: { legalEntityId?: string; partnerId?: string | null },
): Resolutions {
  const next = { ...(all[String(row)] ?? {}), ...patch };
  if (!next.legalEntityId) delete next.legalEntityId;
  if (!next.partnerId) delete next.partnerId;
  const copy = { ...all };
  if (Object.keys(next).length === 0) delete copy[String(row)];
  else copy[String(row)] = next;
  return copy;
}

/** Aviso da lista aberta por um cartão da Visão geral (ADR 0097), com o botão de limpar. */
export function CardFilterNotice({ label, onClear }: { label: string; onClear: () => void }) {
  const { t } = useTranslation();
  return (
    <p role="status" className="flex flex-wrap items-center gap-2 text-sm">
      <span className="text-muted">{t('homeCards.listFilter')}</span>
      <Badge tone="info">{label}</Badge>
      <Button
        variant="secondary"
        onClick={() => {
          onClear();
          window.history.replaceState(window.history.state, '', window.location.pathname);
        }}
      >
        {t('homeCards.clearFilter')}
      </Button>
    </p>
  );
}

/** Filtros vindos do link (painel): empresa e responsável. */
export function linkFilters(): Record<string, string> {
  const search = new URLSearchParams(window.location.search);
  const out: Record<string, string> = {};
  for (const key of ['legalEntityId', 'ownerId', 'legalOwnerId']) {
    const v = search.get(key);
    if (v && /^[0-9a-f-]{36}$/i.test(v)) out[key] = v;
  }
  return out;
}
